import React, { useEffect, useRef, useState } from "react";

interface DotLoaderProps {
  text?: string;
  total?: number | null;
  completed?: number | null;
  startedAt?: string | null;
  timeoutMinutes?: number;
  onDismiss?: () => void;
}

const TIMEOUT_DEFAULT_MINUTES = 10;

const DotLoader = ({
  text = "Loading",
  total = null,
  completed = null,
  startedAt = null,
  timeoutMinutes = TIMEOUT_DEFAULT_MINUTES,
  onDismiss
}: DotLoaderProps) => {
  const [dotCount, setDotCount] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [lastProgressAt, setLastProgressAt] = useState<number>(() => Date.now());
  const lastCompletedRef = useRef<number | null>(completed);
  const dismissButton = onDismiss ? (
    <button
      onClick={onDismiss}
      style={{
        marginTop: '16px',
        padding: '8px 24px',
        backgroundColor: '#fff',
        color: '#000',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: 'bold',
        cursor: 'pointer'
      }}
    >
      Continue in background
    </button>
  ) : null;

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 5);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setTimedOut(false);
    setLastProgressAt(Date.now());
    lastCompletedRef.current = completed;
  }, [startedAt]);

  useEffect(() => {
    if (completed == null) return;
    if (lastCompletedRef.current === null) {
      lastCompletedRef.current = completed;
      return;
    }

    if (completed !== lastCompletedRef.current) {
      lastCompletedRef.current = completed;
      setTimedOut(false);
      setLastProgressAt(Date.now());
    }
  }, [completed]);

  // Treat lack of progress as the timeout signal rather than total runtime.
  useEffect(() => {
    const checkTimeout = () => {
      const elapsed = Date.now() - lastProgressAt;
      if (elapsed > timeoutMinutes * 60 * 1000) {
        setTimedOut(true);
      }
    };

    checkTimeout();
    const interval = setInterval(checkTimeout, 5000);
    return () => clearInterval(interval);
  }, [lastProgressAt, timeoutMinutes]);

  const hasProgress = total != null && total > 0 && completed != null;
  const remaining = hasProgress ? total - completed : 0;

  return (
    <div style={{ display: 'block', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', zIndex: 9999 }}>
      <div style={{
        position: 'absolute',
        top: 'calc(50% + 40px)',
        left: '50%',
        transform: 'translateX(-50%)',
        color: '#fff',
        fontSize: '16px',
        fontWeight: 'bold',
        textAlign: 'center'
      }}>
        {timedOut ? (
          <>
            <div className="text-xl font-medium font-mono" style={{ color: '#fbbf24' }}>
              Report generation is taking longer than expected.
            </div>
            <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 'normal', opacity: 0.9 }}>
              The task is still running in the background. You can hide this and continue browsing.
            </div>
            {dismissButton}
          </>
        ) : (
          <>
            <div className="text-xl font-medium font-mono">
              {text}{".".repeat(dotCount)}
            </div>
            {hasProgress && (
              <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 'normal', opacity: 0.9 }}>
                {completed} / {total} reports completed
                {remaining > 0 && (
                  <span> &mdash; ~{Math.ceil(remaining * 0.5)} min remaining</span>
                )}
              </div>
            )}
            {onDismiss && (
              <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 'normal', opacity: 0.9 }}>
                This keeps running in the background &mdash; you can keep working and we&rsquo;ll let you know when it&rsquo;s done.
              </div>
            )}
            {dismissButton}
          </>
        )}
      </div>
    </div>
  );
};

export default DotLoader;
